import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import StatCard from "@/components/base/StatCard";
import Button from "@/components/base/Button";
import { Input, Select } from "@/components/base/Field";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import Tabs from "@/components/base/Tabs";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import { listActivity } from "@/services/activityService";
import { actionMeta, actionOptions, dayKey, downloadActivityCsv, moduleIcon } from "@/utils/activity";
import ActivityTimeline from "./components/ActivityTimeline";
import type { ActivityLogEntry } from "@/types";
import { plural } from "@/utils/format";

const SCOPE_TABS = [
  { id: "toutes", label: "Toutes", icon: "ri-list-check-2" },
  { id: "jour", label: "Aujourd'hui", icon: "ri-calendar-event-line" },
  { id: "semaine", label: "7 derniers jours", icon: "ri-calendar-2-line" },
];

export default function ActivityPage() {
  const { activeSchool, isPlatformAdmin, schools } = useAuth();
  const { notify } = useToast();
  /** Super-administrateur : école consultée (vide = toutes les écoles). */
  const [schoolFilter, setSchoolFilter] = useState("");

  const loader = useMemo(
    () => () =>
      isPlatformAdmin
        ? listActivity({ platform: true, schoolId: schoolFilter || undefined })
        : listActivity(),
    [isPlatformAdmin, schoolFilter],
  );
  const { data, loading, error, reload } = useAsync<ActivityLogEntry[]>(loader, [loader]);

  const schoolNames = useMemo(
    () => Object.fromEntries(schools.map((s) => [s.id, s.shortName])),
    [schools],
  );

  const [scope, setScope] = useState("toutes");
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("tous");
  const [action, setAction] = useState("toutes");
  const pager = usePager();

  const entries = useMemo(() => data ?? [], [data]);

  const modules = useMemo(
    () => Array.from(new Set(entries.map((e) => e.module))).sort((a, b) => a.localeCompare(b)),
    [entries],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const todayKey = dayKey(new Date().toISOString());
    const weekLimit = Date.now() - 7 * 86400000;

    return entries.filter((e) => {
      const matchQuery =
        !q ||
        e.actorName.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        (e.target ?? "").toLowerCase().includes(q) ||
        e.module.toLowerCase().includes(q);
      const matchModule = module === "tous" || e.module === module;
      const matchAction = action === "toutes" || e.action === action;
      const matchScope =
        scope === "toutes" ||
        (scope === "jour"
          ? dayKey(e.createdAt) === todayKey
          : new Date(e.createdAt).getTime() >= weekLimit);
      return matchQuery && matchModule && matchAction && matchScope;
    });
  }, [entries, search, module, action, scope]);
  const page = pager.slice(filtered, `${scope}|${search}|${module}|${action}|${schoolFilter}`);

  const stats = useMemo(() => {
    const todayKey = dayKey(new Date().toISOString());
    const today = entries.filter((e) => dayKey(e.createdAt) === todayKey).length;
    const actors = new Set(entries.map((e) => e.actorName)).size;
    const moduleCount = new Set(entries.map((e) => e.module)).size;
    return { total: entries.length, today, actors, moduleCount };
  }, [entries]);

  const handleExport = () => {
    if (filtered.length === 0) {
      notify("Aucune action à exporter.", "error");
      return;
    }
    downloadActivityCsv(filtered, `journal-${dayKey(new Date().toISOString())}.csv`, schoolNames);
    notify(`Journal exporté (${plural(filtered.length, "action")}).`);
  };

  const scopeTabs = SCOPE_TABS.map((t) => ({
    ...t,
    count:
      t.id === "jour"
        ? stats.today
        : t.id === "toutes"
          ? stats.total
          : undefined,
  }));

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Historique des actions"
        subtitle={
          isPlatformAdmin
            ? "Journal des actions des écoles de la plateforme (conservé 12 mois)"
            : activeSchool
              ? `Journal des actions — ${activeSchool.name} (conservé 12 mois)`
              : "Journal des actions de l'établissement"
        }
        icon="ri-history-line"
        actions={
          <>
            <Button variant="outline" icon="ri-refresh-line" onClick={reload}>
              Actualiser
            </Button>
            <Button variant="outline" icon="ri-download-2-line" onClick={handleExport}>
              Exporter
            </Button>
          </>
        }
      />
      {isPlatformAdmin && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50/60 p-4">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-700">
            <i className="ri-information-line text-lg"></i>
          </span>
          <p className="text-xs text-foreground-700 leading-relaxed">
            Console super-admin : choisissez une école pour consulter son journal, ou affichez toutes
            les écoles. Les utilisateurs d'une école ne voient que le journal de la leur.
          </p>
          <div className="ml-auto w-full max-w-[220px] shrink-0">
            <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
              <option value="">Toutes les écoles</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.shortName}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState label="Chargement du journal d'activité..." />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Actions enregistrées" value={String(stats.total)} icon="ri-history-line" tone="primary" />
            <StatCard label="Aujourd'hui" value={String(stats.today)} icon="ri-calendar-check-line" tone="accent" />
            <StatCard label="Auteurs actifs" value={String(stats.actors)} icon="ri-user-follow-line" tone="secondary" />
            <StatCard label="Modules concernés" value={String(stats.moduleCount)} icon="ri-apps-2-line" tone="primary" />
          </div>

          <div className="mt-6 mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs tabs={scopeTabs} active={scope} onChange={setScope} />
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
              <div className="w-full sm:max-w-xs">
                <Input
                  placeholder="Rechercher un auteur, une action, une cible..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="w-full sm:max-w-[190px]">
                <Select value={module} onChange={(e) => setModule(e.target.value)}>
                  <option value="tous">Tous les modules</option>
                  {modules.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-full sm:max-w-[180px]">
                <Select value={action} onChange={(e) => setAction(e.target.value)}>
                  <option value="toutes">Toutes les actions</option>
                  {actionOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
            <div className="lg:col-span-3">
              <Card padded={false} className="p-4 md:p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <SectionTitle
                    icon="ri-time-line"
                    title="Chronologie des actions"
                    subtitle="Les plus récentes en premier"
                  />
                  <span className="text-xs text-foreground-500 whitespace-nowrap">
                    {plural(filtered.length, "résultat")}
                  </span>
                </div>
                <ActivityTimeline entries={page.items} schoolNames={isPlatformAdmin ? schoolNames : undefined} />
                <Pagination pager={page} className="mt-3 border-t border-background-200 !px-0" />
              </Card>
            </div>

            <div id="activity-today" className="space-y-5">
              <Card>
                <SectionTitle icon="ri-pie-chart-2-line" title="Répartition par action" />
                <div className="space-y-2.5">
                  {(Object.keys(actionMeta) as (keyof typeof actionMeta)[])
                    .map((key) => ({
                      key,
                      meta: actionMeta[key],
                      count: entries.filter((e) => e.action === key).length,
                    }))
                    .filter((row) => row.count > 0)
                    .sort((a, b) => b.count - a.count)
                    .map((row) => {
                      const pct = stats.total ? Math.round((row.count / stats.total) * 100) : 0;
                      return (
                        <div key={row.key}>
                          <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="flex items-center gap-2 text-foreground-700">
                              <span className="flex h-4 w-4 items-center justify-center">
                                <i className={`${row.meta.icon} text-sm`}></i>
                              </span>
                              {row.meta.label}
                            </span>
                            <span className="tabular-nums text-foreground-500">
                              {row.count} · {pct}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-100">
                            <div
                              className={`h-full rounded-full ${
                                row.meta.tone === "primary"
                                  ? "bg-primary-600"
                                  : row.meta.tone === "accent"
                                    ? "bg-accent-500"
                                    : row.meta.tone === "secondary"
                                      ? "bg-secondary-500"
                                      : "bg-foreground-400"
                              }`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>

              <Card>
                <SectionTitle icon="ri-grid-line" title="Modules les plus actifs" />
                <div className="space-y-2">
                  {modules
                    .map((m) => ({ module: m, count: entries.filter((e) => e.module === m).length }))
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 6)
                    .map((row) => (
                      <div
                        key={row.module}
                        className="flex items-center justify-between rounded-md border border-background-200 bg-background-100/50 px-3 py-2"
                      >
                        <span className="flex items-center gap-2 text-xs text-foreground-700">
                          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-background-200 text-foreground-600">
                            <i className={`${moduleIcon(row.module)} text-sm`}></i>
                          </span>
                          {row.module}
                        </span>
                        <span className="text-xs font-medium tabular-nums text-foreground-800">
                          {row.count}
                        </span>
                      </div>
                    ))}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}